import type { Priority, Status } from "../tasks/types.js";
import { floatingToInstant } from "../zone.js";
import { unescapeText, unfold } from "./escape.js";
import type { IcsDateValue, ImportedItem, ParsedIcs } from "./types.js";

interface PropLine {
  name: string;
  params: Record<string, string>;
  value: string;
}

/** Parse a VCALENDAR string into normalized {@link ImportedItem}s. Pure. */
export function parseIcs(text: string): ParsedIcs {
  const lines = unfold(text).map(parseLine);
  const items: ImportedItem[] = [];
  const errors: string[] = [];
  let calendarName: string | null = null;

  let cur: ImportedItem | null = null;
  let inAlarm = false;
  let inTimezone = false;
  let alarmTrigger: number | null = null;

  for (const p of lines) {
    const v = p.value;
    if (p.name === "BEGIN") {
      if (v === "VTIMEZONE") inTimezone = true;
      else if (v === "VALARM") { inAlarm = true; alarmTrigger = null; }
      else if (v === "VEVENT" || v === "VTODO") cur = blankItem(v === "VEVENT" ? "event" : "todo");
      continue;
    }
    if (p.name === "END") {
      if (v === "VTIMEZONE") inTimezone = false;
      else if (v === "VALARM") { if (cur && alarmTrigger != null) cur.reminders.push(alarmTrigger); inAlarm = false; }
      else if ((v === "VEVENT" || v === "VTODO") && cur) { items.push(cur); cur = null; }
      continue;
    }
    if (inTimezone) continue;
    if (inAlarm) {
      if (p.name === "TRIGGER") alarmTrigger = parseTrigger(p);
      continue;
    }
    if (!cur) {
      if (p.name === "X-WR-CALNAME") calendarName = unescapeText(v);
      continue;
    }
    try {
      applyProp(cur, p);
    } catch {
      errors.push(`could not parse ${p.name}`);
    }
  }

  return { items, calendarName, errors };
}

function applyProp(item: ImportedItem, p: PropLine): void {
  switch (p.name) {
    case "UID": item.uid = p.value; break;
    case "SUMMARY": item.summary = unescapeText(p.value); break;
    case "DESCRIPTION": item.description = unescapeText(p.value); break;
    case "LOCATION": item.location = unescapeText(p.value); break;
    case "DTSTART": item.start = parseDate(p); item.allDay = item.start.allDay; item.tz = item.start.tz; break;
    case "DTEND": item.end = parseDate(p); break;
    case "DUE": item.deadlineAt = parseDate(p).iso; break;
    case "DURATION": item.durationMinutes = parseDurationMinutes(p.value); break;
    case "RRULE": applyRrule(item, p.value); break;
    case "RECURRENCE-ID": item.recurrenceId = parseDate(p); break;
    case "EXDATE": item.exdates.push(...p.value.split(",").map((x) => parseDate({ ...p, value: x }))); break;
    case "CATEGORIES": item.categories.push(...splitList(p.value)); break;
    case "PRIORITY": item.priority = priorityFromNumber(Number(p.value)); break;
    case "STATUS": item.status = statusFromIcs(p.value); break;
    case "X-REMINDERS-IS-EVENT": item.isEvent = p.value.toUpperCase() === "TRUE"; break;
    case "X-REMINDERS-COLOR": item.color = p.value; break;
    case "X-REMINDERS-PRIORITY": item.priority = priorityFromName(p.value); break;
    case "X-REMINDERS-CALENDAR": item.calendarName = unescapeText(p.value); break;
    case "X-REMINDERS-TOPICS": item.topics.push(...splitList(p.value)); break;
  }
  if (item.start && item.tz == null) item.tz = item.start.tz;
}

function applyRrule(item: ImportedItem, raw: string): void {
  const parts = raw.split(";");
  const kept: string[] = [];
  for (const part of parts) {
    const [k, val] = part.split("=");
    if (k?.toUpperCase() === "UNTIL" && val) item.until = parseDate({ name: "UNTIL", params: {}, value: val }).iso;
    else kept.push(part);
  }
  item.rrule = kept.join(";");
}

function parseLine(line: string): PropLine {
  const colon = indexOfValueColon(line);
  const head = line.slice(0, colon);
  const value = line.slice(colon + 1);
  const segs = head.split(";");
  const name = segs[0]!.toUpperCase();
  const params: Record<string, string> = {};
  for (let i = 1; i < segs.length; i++) {
    const [k, val] = segs[i]!.split("=");
    if (k) params[k.toUpperCase()] = (val ?? "").replace(/^"|"$/g, "");
  }
  return { name, params, value };
}

/** First ":" not inside a quoted param value. */
function indexOfValueColon(line: string): number {
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') quoted = !quoted;
    else if (c === ":" && !quoted) return i;
  }
  return line.length;
}

/** Parse a single RFC-5545 date/date-time token (with optional TZID/VALUE
 *  params) to a normalized {@link IcsDateValue}. Exposed for reuse by the Google
 *  Calendar mapper, whose recurrence EXDATE/UNTIL tokens share this grammar. */
export function parseIcsDate(value: string, params: Record<string, string> = {}): IcsDateValue {
  return parseDate({ name: "", params, value });
}

function parseDate(p: PropLine): IcsDateValue {
  const v = p.value.trim();
  const tzid = p.params["TZID"] ?? null;
  if (p.params["VALUE"] === "DATE" || /^\d{8}$/.test(v)) {
    const y = +v.slice(0, 4), mo = +v.slice(4, 6), d = +v.slice(6, 8);
    return { iso: new Date(Date.UTC(y, mo - 1, d)).toISOString(), allDay: true, tz: null };
  }
  const m = v.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})(Z?)$/);
  if (!m) return { iso: new Date(v).toISOString(), allDay: false, tz: tzid };
  const [, Y, Mo, D, H, Mi, S, z] = m;
  if (z === "Z") return { iso: new Date(Date.UTC(+Y!, +Mo! - 1, +D!, +H!, +Mi!, +S!)).toISOString(), allDay: false, tz: null };
  const floating = new Date(Date.UTC(+Y!, +Mo! - 1, +D!, +H!, +Mi!, +S!));
  if (tzid) return { iso: floatingToInstant(floating, tzid), allDay: false, tz: tzid };
  return { iso: floating.toISOString(), allDay: false, tz: null };
}

function parseTrigger(p: PropLine): number | null {
  if (p.params["VALUE"] === "DATE-TIME") return null; // absolute trigger — no offset mapping
  const min = parseDurationMinutes(p.value);
  if (min == null) return null;
  return Math.max(0, -min); // negative duration = "before"; positive/0 → 0
}

/** ISO-8601 duration → signed minutes (e.g. "-PT1H30M" → -90). */
function parseDurationMinutes(s: string): number | null {
  const m = s.match(/^([+-]?)P(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/);
  if (!m) return null;
  const sign = m[1] === "-" ? -1 : 1;
  const min = (+(m[2] ?? 0)) * 10080 + (+(m[3] ?? 0)) * 1440 + (+(m[4] ?? 0)) * 60 + (+(m[5] ?? 0)) + Math.round((+(m[6] ?? 0)) / 60);
  return sign * min;
}

function splitList(v: string): string[] {
  return v.split(",").map((x) => unescapeText(x).trim()).filter(Boolean);
}

function priorityFromNumber(n: number): Priority {
  if (!n) return "none";
  if (n <= 4) return "high";
  if (n === 5) return "medium";
  return "low";
}

function priorityFromName(s: string): Priority {
  return (["none", "low", "medium", "high"] as Priority[]).includes(s as Priority) ? (s as Priority) : "none";
}

function statusFromIcs(s: string): Status {
  const u = s.toUpperCase();
  if (u === "COMPLETED") return "done";
  if (u === "CANCELLED") return "cancelled";
  return "open";
}

function blankItem(kind: "event" | "todo"): ImportedItem {
  return {
    kind, uid: null, summary: "", description: "", location: "",
    start: null, end: null, deadlineAt: null, durationMinutes: null, allDay: false,
    rrule: null, until: null, recurrenceId: null, exdates: [],
    reminders: [], priority: "none", color: null, isEvent: kind === "event",
    status: "open", categories: [], topics: [], calendarName: null, tz: null,
  };
}
